import { z } from 'zod';
import { takeTtsModelInfoSchema } from '#common/models/tts-models';
import { type RecordingModel } from '#speaker-shop/components/RecordingContext';

const STORAGE_VERSION = 2;
const MAX_AGE_MS = 1 * 24 * 60 * 60 * 1000;

const storedTakeSchema = z.object({
    takeId: z.string(),
    text: z.string(),
    isChecked: z.boolean(),
    name: z.string().optional(),
    speakerVoiceId: z.string().optional(),
    filePath: z.string(),
    createdAt: z.string()
}).and(takeTtsModelInfoSchema);

const storedRecordingSchema = z.object({
    name: z.string().optional(),
    text: z.string(),
    takes: z.array(storedTakeSchema),
    createdAt: z.string()
});

const sessionStorageDataSchema = z.object({
    version: z.number(),
    timestamp: z.string(),
    recordings: z.array(storedRecordingSchema)
});

type StoredRecording = z.infer<typeof storedRecordingSchema>;
type SessionStorageData = z.infer<typeof sessionStorageDataSchema>;

/**
 * Generate storage key per speaker
 */
function getStorageKey(speakerSlug: string): string {
    return `recordings_${speakerSlug}`;
}

/**
 * Convert RecordingModel to storable format (removes audio Blob/URL)
 */
function recordingsToStorable(recordings: RecordingModel[]): StoredRecording[] {
    const now = new Date().toISOString();
    return recordings.map(recording => ({
        name: recording.name,
        text: recording.text,
        createdAt: (recording as any).createdAt || now,
        takes: recording.takes.map(take => {
            const { audio, ...rest } = take;
            return {
                ...rest,
                createdAt: (take as any).createdAt || now
            };
        })
    }));
}

/**
 * Save recordings to sessionStorage
 */
export function saveRecordingsToStorage(recordings: RecordingModel[], speakerSlug: string): void {
    try {
        const data: SessionStorageData = {
            version: STORAGE_VERSION,
            timestamp: new Date().toISOString(),
            recordings: recordingsToStorable(recordings)
        };

        const key = getStorageKey(speakerSlug);
        sessionStorage.setItem(key, JSON.stringify(data));
        console.log('✅ Recordings saved to sessionStorage');
    } catch (error) {
        console.error('Failed to save recordings to sessionStorage:', error);
        // Don't throw - sessionStorage might be disabled
    }
}

/**
 * Load recordings from sessionStorage
 * Returns null if no data, invalid data, or error
 */
export function loadRecordingsFromStorage(speakerSlug: string): StoredRecording[] | null {
    try {
        const key = getStorageKey(speakerSlug);
        const raw = sessionStorage.getItem(key);

        if (!raw) {
            console.log('No recordings in sessionStorage');
            return null;
        }

        const data = sessionStorageDataSchema.parse(JSON.parse(raw));

        if (data.version !== STORAGE_VERSION) {
            console.warn('SessionStorage data has wrong version, ignoring');
            sessionStorage.removeItem(key);
            return null;
        }

        const validRecordings = data.recordings.filter(rec => {
            const recordingAge = Date.now() - new Date(rec.createdAt).getTime();
            if (recordingAge > MAX_AGE_MS) {
                console.warn(`Recording too old (${Math.floor(recordingAge / 1000 / 60 / 60)} hours), skipping`);
                return false;
            }
            return true;
        });

        if (validRecordings.length === 0) {
            console.log('All recordings expired, clearing storage');
            sessionStorage.removeItem(key);
            return null;
        }

        if (validRecordings.length < data.recordings.length) {
            // Some recordings expired, update storage with only valid ones
            data.recordings = validRecordings;
            sessionStorage.setItem(key, JSON.stringify(data));
            console.log(`Removed ${data.recordings.length - validRecordings.length} expired recordings`);
        }

        console.log('✅ Recordings loaded from sessionStorage');
        return validRecordings;
    } catch (error) {
        console.error('Failed to load recordings from sessionStorage:', error);
        return null;
    }
}

/**
 * Clear recordings from sessionStorage
 */
export function clearRecordingsFromStorage(speakerSlug: string): void {
    try {
        const key = getStorageKey(speakerSlug);
        sessionStorage.removeItem(key);
        console.log('✅ Recordings cleared from sessionStorage');
    } catch (error) {
        console.error('Failed to clear recordings from sessionStorage:', error);
    }
}

/**
 * Check if there are recordings in storage
 */
export function hasStoredRecordings(speakerSlug: string): boolean {
    try {
        const key = getStorageKey(speakerSlug);
        return sessionStorage.getItem(key) !== null;
    } catch (error) {
        return false;
    }
}

export function hasLoadedAudio(audio: string | Blob) {
    if (typeof audio === 'string') {
        return audio.length > 0;
    }
    return audio instanceof Blob && audio.size > 0;
}

export function isRestoredTake({ audio, takeId }: { audio: string | Blob; takeId: string }) {
    return !hasLoadedAudio(audio) && takeId.length > 0;
}
